import {inject, Injectable} from '@angular/core';
import {MenuItem, MenuUtils} from '../../lib/wjmenu';
import {FlexGrid} from '@mescius/wijmo.grid';
import {Menu} from '@mescius/wijmo.input';
import {FlexGridFilter} from '@mescius/wijmo.grid.filter';
import {AuthService} from '../../security/auth.service';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';
import {KeyValueAndParamsService} from '../../service/key-value-and-params.service';
import {AccountGridService, AppAccountColumnDeclaration} from './account-grid-helper';
import {AppGridViewService} from '../../service/app-grid-view.service';
import {
    AccountGridViewEditorComponent
} from '../../components/account-grid-view-editor/account-grid-view-editor.component';
import {NgDynamicComponentsService} from '../../service/ng-dynamic-components.service';
import {AccountDataWithChecks} from '../../data/app-data-checks';
import {WjGridChangeTracker} from '../../lib/wjgrid-change-tracker';
import {WjGridColumnChooser} from '../../lib/wjgrid-columchooser';

type CtxMenuCommand = 'my-views' | 'save-view' | 'open-view' | 'share-view' | 'nop' | 'clear-filters' | 'reset-sort' | 'row-header';

export interface AccountGridContainerComponent {
    getPfSegData(): PortfolioSegmentDataContext;
    getGrid(): FlexGrid;
    getGridFilter(): FlexGridFilter;
    setHeaderBinding(binding: string): void;
    showCellCommentsEditor(item: AccountDataWithChecks, binding: string, cell: HTMLElement): Promise<void>
    getChangeTracker(): WjGridChangeTracker;
}

const menuItems: MenuItem<CtxMenuCommand>[] = MenuUtils.expandMenuTree([
    { header: 'Open View', iconCls: 'far fa-gallery-thumbnails', cmd: 'nop',
        items: [
            { header: 'Default', iconCls: 'far fa-columns-3', cmd: 'open-view', data: 'default' },
            { header: 'Minimalistic', iconCls: 'far fa-table-layout', cmd: 'open-view', data: 'minimalistic' },
            { header: 'Tiering', iconCls: 'far fa-line-columns', cmd: 'open-view', data: 'tiering' },
            { header: 'Core Data', iconCls: 'far fa-bars', cmd: 'open-view', data: 'debug-compare' },
            //{ header: 'Overview', iconCls: 'far fa-line-columns', cmd: 'open-view', data: 'overview' },
        ]
    },
    { header: 'My Views', iconCls: 'far fa-bookmark', cmd: 'my-views' },
    { header: 'Save Current View', iconCls: 'far fa-bookmark-plus', cmd: 'save-view' },
    { header: 'Share Current View', iconCls: 'far fa-share-nodes', cmd: 'share-view' },
    { header: '-', },
    { header: 'Row Header', iconCls: 'far fa-list-ul', cmd: 'nop',
        items: [
            { header: 'Row Number', iconCls: 'far fa-hashtag', cmd: 'row-header', data: 'row' },
            { header: 'Account Name', iconCls: 'far fa-buildings', cmd: 'row-header', data: 'account' },
            { header: 'AGORA ID', iconCls: 'far fa-input-text', cmd: 'row-header', data: 'uwbId' },
            { header: 'Submission ID', iconCls: 'far fa-input-numeric', cmd: 'row-header', data: 'crmSubmissionId' }
        ]
    },
    { header: '-', },
    { header: 'Clear Filters', iconCls: 'far fa-filter-circle-xmark', cmd: 'clear-filters' },
    { header: 'Reset Sort', iconCls: 'far fa-arrow-up-short-wide', cmd: 'reset-sort' },
]);

export class AccountMenu {
    menu!: Menu;
    accountGridComponent: AccountGridContainerComponent;

    constructor(accountGridComponent: AccountGridContainerComponent) {
        this.accountGridComponent = accountGridComponent;
    }

    getGrid(): FlexGrid {
        return this.accountGridComponent.getGrid();
    }

    getGridFilter(): FlexGridFilter {
        return this.accountGridComponent.getGridFilter();
    }

    getPfSegData(): PortfolioSegmentDataContext {
        return this.accountGridComponent.getPfSegData();
    }

    getChangeTracker(): WjGridChangeTracker {
        return this.accountGridComponent.getChangeTracker();
    }

    show(e: any) {
        this.menu.show(e);
    }
}

@Injectable({
    providedIn: 'root'
})
export class AccountGridCtxMenuService {

    private authService = inject(AuthService);
    private pocketBaseService = inject(KeyValueAndParamsService);
    private readonly dynCompSvc = inject(NgDynamicComponentsService);
    private readonly appGridViewService = inject(AppGridViewService);

    constructor() {
    }

    createMenu(hostEl: Element, accountGridContainerComponent: AccountGridContainerComponent): AccountMenu {
        const menuCtx = new AccountMenu(accountGridContainerComponent);
        const menu = new Menu(hostEl, {
                showDropDownButton: false,
                isContentHtml: true,
                displayMemberPath: 'headerHtml',
                selectedValuePath: 'cmd',
                dropDownCssClass: 'grid-ctx-menu',
                subItemsPath: 'items',
                itemsSource: menuItems,
                command: {
                    executeCommand: this.onRowCommand.bind(this, menuCtx),
                    canExecuteCommand: this.canExecuteRowCommand.bind(this, menuCtx),
                }
            }
        );
        menuCtx.menu = menu;
        return menuCtx;
    }

    private async onRowCommand(menu: AccountMenu, menuItem: MenuItem<CtxMenuCommand>) {
        switch (menuItem.cmd) {
            case 'my-views': await this.onMyBookmarks(menu); break;
            case 'save-view': await this.onSaveGridLayout(menu); break;
            case 'share-view': await this.onShareGridView(menu); break;
            case 'open-view': await this.onOpenGridView(menu, menuItem); break;
            case 'clear-filters': this.clearFilters(menu); break;
            case 'reset-sort':  this.resetSorting(menu); break;
            case 'row-header': this.onSetRowHeader(menu, menuItem); break;
        }
    }

    private resetSorting(menu: AccountMenu) {
        const view = menu.getGrid().collectionView;
        view.sortDescriptions.clear();
        view.refresh();
    }

    private clearFilters(menu: AccountMenu) {
        menu.getGridFilter().clear();
        menu.getGrid().collectionView.refresh();
    }

    private async onMyBookmarks(menu: AccountMenu) {
        const dialog = await AccountGridViewEditorComponent.showDialog(this.dynCompSvc, {
            width: '400px',
            height: 'calc(min(500px, 80vh))',
            input: { mode: 'view' }
        });
        dialog.acceptedItem.subscribe((item) => {
            const layout = this.appGridViewService.loadGridLayout(item.reference!);
            AppGridViewService.restoreLayout(menu.getGrid(), menu.getGridFilter(), layout, menu.getPfSegData())
        });
    }

    private async onSaveGridLayout(menu: AccountMenu) {
        const layout = AppGridViewService.takeLayoutSnapshot(menu.getGrid(), menu.getGridFilter(), true);
        const key = this.appGridViewService.saveGridLayout(layout);
        await AccountGridViewEditorComponent.showDialog(this.dynCompSvc, {
            width: '400px',
            height: 'calc(min(500px, 80vh))',
            input: { mode: 'add', newItemKey: key }
        });

    }

    private async onShareGridView(menu: AccountMenu) {
        const layout = AppGridViewService.takeLayoutSnapshot(menu.getGrid(), menu.getGridFilter(), true);
        await this.appGridViewService.showShareGridViewDialog(layout);
    }

    canExecuteRowCommand(menu: AccountMenu, menuItem: MenuItem<CtxMenuCommand>): boolean {
        return true;
    }

    private async onOpenGridView(menu: AccountMenu, menuItem: MenuItem<CtxMenuCommand>) {
        let view: AppAccountColumnDeclaration[] | undefined;
        switch( menuItem.data ) {
            case 'default':
                view = AccountGridService.getStandardViewConfiguration();
                break;
            case 'tiering':
                view = AccountGridService.getTieringViewConfiguration();
                break;
            case 'overview':
                view = AccountGridService.getStandardViewConfiguration();
                break;
            case 'minimalistic':
                view = AccountGridService.getMinimalisticViewConfiguration();
                break;
            case 'debug-compare':
                view = AccountGridService.getDebugCompareViewConfiguration();
                break;
        }
        if (view) {
            await AccountGridService.configureGridColumns(menu.getGrid(), view, menu.getPfSegData());
            WjGridColumnChooser.addColumnChooser('account-grid', menu.getGrid());
        } else {
            console.warn(`No columns defined for the selected view: ${menuItem.data}`);
        }
    }

    private onSetRowHeader(menu: AccountMenu, menuItem: MenuItem<CtxMenuCommand>) {
        const headerBinding = menuItem.data;
        menu.accountGridComponent.setHeaderBinding(headerBinding);
    }
}
